import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { Trophy, Save, AlertTriangle, RefreshCw, DownloadCloud } from 'lucide-react';
import { Button } from '../../components/ui';

export const WkAdminPage = () => {
    const { t } = useTranslation();
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [results, setResults] = useState({});

    useEffect(() => {
        fetchMatches();
    }, []);

    const fetchMatches = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('wk_matches')
                .select(`
                    id, 
                    kickoff_time, 
                    home_score, 
                    away_score, 
                    status,
                    home_team:home_team_id(id, name),
                    away_team:away_team_id(id, name)
                `)
                .order('kickoff_time', { ascending: true });
            
            if (error) throw error;
            setMatches(data);
            
            // Initialize results state with existing scores
            const resMap = {};
            data.forEach(m => {
                resMap[m.id] = {
                    home: m.home_score !== null ? m.home_score : '',
                    away: m.away_score !== null ? m.away_score : '',
                    status: m.status
                };
            });
            setResults(resMap);
        } catch (err) {
            console.error('Error fetching admin matches:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleApiSync = async () => {
        if (!window.confirm('Weet je zeker dat je alle data live wilt synchroniseren met de FIFA API? Dit update automatisch alle uitslagen en teams.')) return;
        
        setSyncing(true);
        try {
            // Get current Supabase session token to verify admin rights on the server
            const { data: { session } } = await supabase.auth.getSession();
            const sessionToken = session?.access_token;

            const headers = {};
            if (sessionToken) {
                headers['Authorization'] = `Bearer ${sessionToken}`;
            }

            // Call the secure backend serverless endpoint
            const response = await fetch('/api/wk-sync', {
                method: 'POST',
                headers
            });
            
            const result = await response.json();
            if (!response.ok) {
                const debugInfo = result.debug ? `\n\nDebug Info: ${JSON.stringify(result.debug)}` : '';
                throw new Error((result.error || `Server Fout: ${response.status}`) + debugInfo);
            }

            alert(`Succes! ${result.count || 0} wedstrijden gesynchroniseerd met de live API!`);
            fetchMatches(); // Refresh main list
        } catch (err) {
            console.error('Fout bij synchronisatie:', err);
            alert('Er ging iets mis bij het ophalen van live data: ' + err.message);
        } finally {
            setSyncing(false);
        }
    };

    const handleResultChange = (matchId, field, value) => {
        setResults(prev => ({
            ...prev,
            [matchId]: {
                ...prev[matchId],
                [field]: (field === 'home' || field === 'away') ? (value === '' ? '' : parseInt(value, 10)) : value
            }
        }));
    };

    const saveMatchResult = async (matchId) => {
        const res = results[matchId];
        if (res.home === '' || res.away === '') {
            alert('Vul beide scores in.');
            return;
        }

        setSaving(true);
        try {
            // Update the match. Our DB Trigger `tr_update_points` will automatically 
            // calculate and assign points to all users based on this update!
            const { error } = await supabase
                .from('wk_matches')
                .update({
                    home_score: res.home,
                    away_score: res.away,
                    status: res.status
                })
                .eq('id', matchId);

            if (error) throw error;
            
            alert('Uitslag opgeslagen! Punten zijn automatisch berekend voor alle gebruikers.');
            fetchMatches(); // Refresh state
        } catch (err) {
            console.error('Error saving result:', err);
            alert('Fout bij opslaan: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-10 flex justify-center"><RefreshCw className="animate-spin text-orange-500" /></div>;

    return (
        <div className="max-w-6xl mx-auto p-8">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-orange-100 rounded-xl text-orange-600">
                        <Trophy size={28} />
                    </div>
                    <h1 className="text-3xl font-black text-slate-900">
                        WK Poule Admin
                    </h1>
                </div>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-4">
                    <p className="text-slate-600 text-lg max-w-2xl">
                        Voer hier de échte uitslagen in of synchroniseer met de live API. Het systeem verdeelt direct de punten onder alle deelnemers.
                    </p>
                    <Button 
                        variant="primary" 
                        className="bg-blue-600 hover:bg-blue-700 text-white shadow-md flex-shrink-0 font-black"
                        onClick={handleApiSync}
                        disabled={syncing}
                        icon={<DownloadCloud className={syncing ? "animate-bounce" : ""} size={18} />}
                    >
                        {syncing ? "Synchroniseren..." : "Live API Update"}
                    </Button>
                </div>
                
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-4 items-start">
                    <div className="bg-amber-100 p-2 rounded-lg text-amber-600 mt-1">
                        <AlertTriangle size={20} />
                    </div>
                    <div>
                        <strong className="text-amber-900 block font-bold text-base">Let op: SuperAdmin Rechten</strong>
                        <span className="text-amber-800 text-sm">
                            Acties die je hier uitvoert zijn direct live en beïnvloeden de ranglijst van <strong>alle gebruikers</strong> wereldwijd.
                        </span>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 grid grid-cols-12 gap-4 font-bold text-slate-500 text-xs uppercase tracking-wider">
                    <div className="col-span-3">Datum & Tijd</div>
                    <div className="col-span-6 text-center">Wedstrijd</div>
                    <div className="col-span-3 text-right">Status / Actie</div>
                </div>
                
                <div className="divide-y divide-slate-100">
                    {matches.length === 0 ? (
                        <div className="p-12 text-center text-slate-400">
                            Geen wedstrijden gevonden.
                        </div>
                    ) : (
                        matches.map(match => (
                            <div key={match.id} className="px-6 py-6 grid grid-cols-12 gap-4 items-center hover:bg-slate-50/50 transition-colors">
                                <div className="col-span-3">
                                    <div className="font-bold text-slate-900">
                                        {new Date(match.kickoff_time).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
                                    </div>
                                    <div className="text-slate-500 text-sm">
                                        {new Date(match.kickoff_time).toLocaleTimeString('nl-NL', { hour: '2-digit', minute:'2-digit' })} uur
                                    </div>
                                </div>
                                
                                <div className="col-span-6 flex items-center justify-center gap-6">
                                    <div className="flex items-center gap-3 w-40 justify-end">
                                        <span className="font-bold text-lg text-slate-900 truncate">{match.home_team?.name}</span>
                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 shadow-sm shrink-0">
                                            {match.home_team?.flag_url ? (
                                                <img src={match.home_team.flag_url} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="font-bold text-xs text-slate-600">{match.home_team?.name?.substring(0,2)}</span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="flex gap-3 items-center bg-slate-100 p-2 rounded-xl border border-slate-200 shadow-inner">
                                        <input 
                                            type="number" 
                                            min="0"
                                            className="w-14 h-12 bg-white border-2 border-slate-200 rounded-lg text-center text-xl font-black text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 transition-all outline-none shadow-sm"
                                            value={results[match.id]?.home ?? ''}
                                            onChange={(e) => handleResultChange(match.id, 'home', e.target.value)}
                                            placeholder="-"
                                        />
                                        <span className="text-slate-400 font-bold text-xl">:</span>
                                        <input 
                                            type="number" 
                                            min="0"
                                            className="w-14 h-12 bg-white border-2 border-slate-200 rounded-lg text-center text-xl font-black text-slate-900 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 transition-all outline-none shadow-sm"
                                            value={results[match.id]?.away ?? ''}
                                            onChange={(e) => handleResultChange(match.id, 'away', e.target.value)}
                                            placeholder="-"
                                        />
                                    </div>
                                    
                                    <div className="flex items-center gap-3 w-40 justify-start">
                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 shadow-sm shrink-0">
                                            {match.away_team?.flag_url ? (
                                                <img src={match.away_team.flag_url} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="font-bold text-xs text-slate-600">{match.away_team?.name?.substring(0,2)}</span>
                                            )}
                                        </div>
                                        <span className="font-bold text-lg text-slate-900 truncate">{match.away_team?.name}</span>
                                    </div>
                                </div>
                                
                                <div className="col-span-3 flex justify-end items-center gap-3">
                                    <select 
                                        value={results[match.id]?.status || 'scheduled'}
                                        onChange={(e) => handleResultChange(match.id, 'status', e.target.value)}
                                        className={`text-xs font-bold border rounded-md px-2 py-1.5 outline-none cursor-pointer ${
                                            results[match.id]?.status === 'live' ? 'bg-red-50 border-red-200 text-red-700' :
                                            results[match.id]?.status === 'finished' ? 'bg-green-50 border-green-200 text-green-700' :
                                            'bg-slate-50 border-slate-200 text-slate-600'
                                        }`}
                                    >
                                        <option value="scheduled">Gepland</option>
                                        <option value="live">🔴 LIVE</option>
                                        <option value="finished">🏁 Afgelopen</option>
                                    </select>
                                    <Button 
                                        variant={match.status === 'finished' ? 'outline' : 'primary'} 
                                        className={`shadow-sm font-bold whitespace-nowrap ${match.status === 'finished' ? 'border-slate-300 text-slate-600 hover:bg-slate-50' : 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-500/20'}`}
                                        onClick={() => saveMatchResult(match.id)}
                                        disabled={saving}
                                        icon={match.status === 'finished' ? <RefreshCw size={16} /> : <Save size={16} />}
                                    >
                                        {match.status === 'finished' ? 'Update' : 'Definitief'}
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

